import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, Search, AlertCircle, Code, ChevronDown, ChevronUp, Tag } from 'lucide-react';
import { TableLoader } from '../../components/Common/Loader';
import { CustomDropdown } from '../../components/Common/CustomDropdown';
import {
  getProducts,
  getDistributorMarginAPI,
  getRetailerMarginAPI,
  getTradeMarginAPI,
  type ProductItem,
  type MarginItem,
} from '../../api/products';
import { getZonesAPI, getCitiesAPI } from '../../api/locations';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

export type MarginType = 'distributor' | 'retailer' | 'trade';

interface MarginPageProps {
  type: MarginType;
  title: string;
}

export const MarginPage: React.FC<MarginPageProps> = ({ type, title }) => {
  // Dropdown options state
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [zones, setZones] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);

  // Selected filters
  const [selectedProductName, setSelectedProductName] = useState<string>('All Products');
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);

  const [selectedZoneName, setSelectedZoneName] = useState<string>('WEST');
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(1);

  const [selectedCityName, setSelectedCityName] = useState<string>('Mumbai 1');
  const [selectedCityId, setSelectedCityId] = useState<number | null>(10);

  // Margin data state from API
  const [marginList, setMarginList] = useState<MarginItem[]>([]);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // In-table search and pagination
  const [tableSearch, setTableSearch] = useState<string>('');
  const [showRawJson, setShowRawJson] = useState<boolean>(false);

  // 1. Fetch dropdown lists (Products, Zones, Cities) on mount
  useEffect(() => {
    let isMounted = true;
    const fetchDropdowns = async () => {
      try {
        const [prodRes, zonesRes, citiesRes] = await Promise.allSettled([
          getProducts(),
          getZonesAPI(),
          getCitiesAPI(),
        ]);

        if (!isMounted) return;

        // Populate Products dropdown options
        if (prodRes.status === 'fulfilled' && prodRes.value?.data) {
          const pList = prodRes.value.data.products || (Array.isArray(prodRes.value.data) ? prodRes.value.data : []);
          setProducts(pList);
        }

        // Populate Zones
        if (zonesRes.status === 'fulfilled' && zonesRes.value) {
          const zList = zonesRes.value.data || zonesRes.value.zones || (Array.isArray(zonesRes.value) ? zonesRes.value : []);
          if (Array.isArray(zList)) {
            const mappedZones = zList.map((z: any) => ({ id: z.id, name: z.name }));
            setZones(mappedZones);
            const foundWest = mappedZones.find((z: any) => z.id === 1 || z.name.toUpperCase() === 'WEST');
            if (foundWest) {
              setSelectedZoneName(foundWest.name);
              setSelectedZoneId(foundWest.id);
            }
          }
        }

        // Populate Cities
        if (citiesRes.status === 'fulfilled' && citiesRes.value) {
          const cList = citiesRes.value.data || citiesRes.value.cities || (Array.isArray(citiesRes.value) ? citiesRes.value : []);
          if (Array.isArray(cList)) {
            const mappedCities = cList.map((c: any) => ({ id: c.id, name: c.name }));
            setCities(mappedCities);
            const foundMumbai = mappedCities.find((c: any) => c.id === 10 || c.name.toLowerCase().includes('mumbai 1'));
            if (foundMumbai) {
              setSelectedCityName(foundMumbai.name);
              setSelectedCityId(foundMumbai.id);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dropdowns for margin page:', err);
      }
    };

    fetchDropdowns();
    return () => {
      isMounted = false;
    };
  }, []);

  // 2. Fetch Margin Data from API
  // By default: calls without productId (GET /api/v1/erp/products/...-margins)
  // If a product is selected: calls with ?productId=55
  const fetchMargins = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (selectedProductId) {
        params.productId = selectedProductId;
      }
      if (selectedZoneId) {
        params.zoneId = selectedZoneId;
      }
      if (selectedCityId) {
        params.cityId = selectedCityId;
      }

      let res: any = null;
      if (type === 'distributor') {
        res = await getDistributorMarginAPI(params);
      } else if (type === 'retailer') {
        res = await getRetailerMarginAPI(params);
      } else if (type === 'trade') {
        res = await getTradeMarginAPI(params);
      }

      setApiResponse(res);

      let items: MarginItem[] = [];
      if (res && res.data) {
        if (Array.isArray(res.data)) {
          items = res.data;
        } else if (typeof res.data === 'object') {
          // If single item or wrapped
          if (Array.isArray(res.data.margins)) {
            items = res.data.margins;
          } else {
            items = [res.data];
          }
        }
      }
      setMarginList(items);
    } catch (err: any) {
      console.error(`Error fetching ${type} margins:`, err);
      setError(err?.message || `Failed to fetch ${type} margins from API.`);
      setMarginList([]);
      setApiResponse(null);
    } finally {
      setLoading(false);
    }
  }, [type, selectedProductId, selectedZoneId, selectedCityId]);

  useEffect(() => {
    fetchMargins();
  }, [fetchMargins]);

  // Dropdown change handlers
  const handleProductChange = (prodName: string) => {
    setSelectedProductName(prodName);
    if (prodName === 'All Products') {
      setSelectedProductId(null);
      return;
    }
    const found = products.find((p) => p.name === prodName);
    setSelectedProductId(found ? found.id : null);
  };

  const handleZoneChange = (zName: string) => {
    setSelectedZoneName(zName);
    if (zName === 'All Zones') {
      setSelectedZoneId(null);
      return;
    }
    const found = zones.find((z) => z.name === zName);
    setSelectedZoneId(found ? found.id : null);
  };

  const handleCityChange = (cName: string) => {
    setSelectedCityName(cName);
    if (cName === 'All Cities') {
      setSelectedCityId(null);
      return;
    }
    const found = cities.find((c) => c.name === cName);
    setSelectedCityId(found ? found.id : null);
  };

  // Dropdown options
  const productDropdownOptions = useMemo(
    () => ['All Products', ...products.map((p) => p.name)],
    [products]
  );
  const zoneDropdownOptions = useMemo(
    () => ['All Zones', ...zones.map((z) => z.name)],
    [zones]
  );
  const cityDropdownOptions = useMemo(
    () => ['All Cities', ...cities.map((c) => c.name)],
    [cities]
  );

  // Client search filter on table
  const filteredMargins = useMemo(() => {
    if (!tableSearch.trim()) return marginList;
    const q = tableSearch.toLowerCase();
    return marginList.filter((item) => {
      const idMatch = String(item.id || '').includes(q);
      const nameMatch = String(item.product?.name || item.product?.displayName || '').toLowerCase().includes(q);
      const sapMatch = String(item.product?.sapCode || '').toLowerCase().includes(q);
      const amountMatch = String(item.margin_amount ?? item.discount ?? item.marginAmount ?? '').includes(q);
      return idMatch || nameMatch || sapMatch || amountMatch;
    });
  }, [marginList, tableSearch]);

  const handleExportCSV = () => {
    const headers = ['ID', 'Product Name', 'SAP Code', 'Margin Value', 'Discount', 'Status', 'Effective From', 'Effective To'];
    const rows = filteredMargins.map((item) => [
      item.id,
      item.product?.name || item.product?.displayName || '',
      item.product?.sapCode || '',
      item.margin_amount ?? item.marginAmount ?? '',
      item.discount ?? '',
      item.status || 'Active',
      item.effectiveFrom || '',
      item.effectiveTo || '',
    ]);
    exportToCSV(`${type}_margins`, headers, rows);
  };

  return (
    <div className="space-y-6 pb-6 select-none animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">{title}</h1>
            <p className="text-xs font-semibold text-slate-400 mt-0.5">
              Live response data for {title.toLowerCase()} from API
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Control / Filter Toolbar */}
      <div className="bg-white rounded-3xl p-6 border border-[#ECECF2] shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-5">
        {/* Product Filter (Optional, defaults to All Products) */}
        <div>
          <CustomDropdown
            label="Product (Optional)"
            placeholder="All Products"
            options={productDropdownOptions}
            value={selectedProductName}
            onChange={handleProductChange}
          />
        </div>

        {/* Zone Filter (Optional) */}
        <div>
          <CustomDropdown
            label="Zone (Optional)"
            placeholder="All Zones"
            options={zoneDropdownOptions}
            value={selectedZoneName}
            onChange={handleZoneChange}
          />
        </div>

        {/* City Filter (Optional) */}
        <div>
          <CustomDropdown
            label="City (Optional)"
            placeholder="All Cities"
            options={cityDropdownOptions}
            value={selectedCityName}
            onChange={handleCityChange}
          />
        </div>
      </div>

      {/* Table Card for Margin API Response */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        {/* Table Header & Controls */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white">
          <div className="flex items-center gap-2.5">
            <Tag className="w-5 h-5 text-[#C1272D]" />
            <h3 className="text-base font-bold text-slate-800">
              {title} Records ({filteredMargins.length})
            </h3>
            
          </div>

          {/* Quick Search */}
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Search product, SAP, ID..."
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
            />
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto min-h-[250px] relative">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <TableLoader />
            </div>
          ) : error ? (
            <div className="py-16 text-center text-rose-500 text-xs font-semibold flex flex-col items-center gap-3">
              <AlertCircle className="w-8 h-8 text-rose-400" />
              <p>{error}</p>
              <button
                onClick={fetchMargins}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                Retry
              </button>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  <th className="py-3.5 px-6">ID</th>
                  <th className="py-3.5 px-6">Product Name</th>
                  <th className="py-3.5 px-6">SAP Code</th>
                  <th className="py-3.5 px-6 text-right">Margin Amount (₹)</th>
                  <th className="py-3.5 px-6 text-center">Status</th>
                  <th className="py-3.5 px-6">Created At</th>
                  <th className="py-3.5 px-6">Updated At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {filteredMargins.map((item) => {
                  const marginVal = item.margin_amount ?? item.discount ?? item.marginAmount ?? '—';
                  const isActive = item.status === 1 || (item.status as any) === '1' || (item.status as any) === true;

                  return (
                    <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-4 px-6 font-bold text-slate-400">{item.id}</td>

                      <td className="py-4 px-6">
                        <span className="font-bold text-slate-900 block">
                          {item.product?.name || item.product?.displayName || '—'}
                        </span>
                        {item.product?.id && (
                          <span className="text-[10px] text-slate-400 font-mono">
                            Product ID: {item.product.id}
                          </span>
                        )}
                      </td>

                      <td className="py-4 px-6 font-mono font-medium text-slate-600">
                        {item.product?.sapCode || '—'}
                      </td>

                      <td className="py-4 px-6 text-right font-extrabold text-[#C1272D] text-sm">
                        {marginVal !== '—' ? `₹${Number(marginVal).toFixed(2)}` : '—'}
                      </td>

                      <td className="py-4 px-6 text-center text-xs text-[#828282]">{isActive ? 'Active' : 'Inactive'}</td>

                      <td className="py-4 px-6 font-medium text-slate-400 whitespace-nowrap">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>

                      <td className="py-4 px-6 font-medium text-slate-400 whitespace-nowrap">
                        {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  );
                })}

                {filteredMargins.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-14 text-center text-slate-400 font-medium">
                      No margin records returned by the API.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Collapsible Raw API Response in table footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex flex-col gap-2">
          <button
            onClick={() => setShowRawJson(!showRawJson)}
            className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors cursor-pointer w-fit"
          >
            <Code className="w-4 h-4 text-slate-400" />
            <span>{showRawJson ? 'Hide Raw API Response' : 'View Raw API Response'}</span>
            {showRawJson ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showRawJson && (
            <div className="mt-2 p-4 bg-slate-900 text-emerald-400 rounded-2xl font-mono text-xs overflow-x-auto shadow-inner">
              <pre>{JSON.stringify(apiResponse, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarginPage;
